import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, Modal, Textarea, toast, UniversalSelect } from '@feathr/components';

import type { IAdGroupKeyword } from '../AdStep.types';
import { DEFAULT_KEYWORD_MATCH_TYPE } from '../AdStep.types';
import type { IBulkAddSummary } from './BulkAddKeywordsModal.utils';
import {
  getBulkAddResult,
  getBulkAddSummaries,
  parseKeywordEntries,
} from './BulkAddKeywordsModal.utils';
import type { IMatchTypeOption } from './KeywordFields.utils';
import { getMatchTypeOptions } from './KeywordFields.utils';

import * as styles from './AdGroupEditor.css';

interface IBulkAddKeywordsModalProps {
  opened: boolean;
  onClose: () => void;
  /** The ad group's current positive keywords, so duplicates can be skipped. */
  existingKeywords: IAdGroupKeyword[];
  onAdd: (keywords: IAdGroupKeyword[]) => void;
}

/**
 * Adds a whole keyword list to one ad group at a single match type. Customers arrive with
 * lists already prepared, and the type-and-add row beside this takes them one at a time.
 *
 * An add can partly succeed. When every entry lands there is nothing to read, so it confirms
 * and closes; when anything was skipped or rejected it stays open and reports what happened,
 * so the list can be fixed without retyping it.
 *
 * @param props - {@link IBulkAddKeywordsModalProps}.
 */
function BulkAddKeywordsModal({
  opened,
  onClose,
  existingKeywords,
  onAdd,
}: Readonly<IBulkAddKeywordsModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [raw, setRaw] = useState('');
  const [matchType, setMatchType] = useState(DEFAULT_KEYWORD_MATCH_TYPE);
  /*
   * Built when the add runs rather than during render, so the report keeps describing the add
   * that produced it even if the field or the picker moves afterwards.
   */
  const [summaries, setSummaries] = useState<IBulkAddSummary[]>([]);
  const [hasAdded, setHasAdded] = useState(false);

  const matchTypeOptions = getMatchTypeOptions(t);
  const selectedMatchType = matchTypeOptions.find((option) => option.id === matchType);
  const hasEntries = !!parseKeywordEntries(raw).length;

  function handleClose(): void {
    setRaw('');
    setMatchType(DEFAULT_KEYWORD_MATCH_TYPE);
    setSummaries([]);
    setHasAdded(false);
    onClose();
  }

  function handleAdd(): void {
    const result = getBulkAddResult(raw, matchType, existingKeywords);
    if (result.added.length) {
      onAdd(result.added);
      setHasAdded(true);
    }

    /* Anything the list did not add is worth reporting, even when nothing needs fixing. */
    if (!result.skipped.length && !result.rejected.length) {
      toast(t('Added {{count}} keyword.', { count: result.added.length }), { type: 'success' });
      handleClose();
      return;
    }

    /*
     * Keep only what still needs attention. Leaving an entry that landed would offer it for a
     * second submission, which at another match type would add it all over again.
     */
    setRaw(result.unresolved.join('\n'));
    setSummaries(getBulkAddSummaries(result, t));
  }

  function handleRawChange(value?: string): void {
    setRaw(value ?? '');
  }

  function handleMatchTypeChange(option: IMatchTypeOption): void {
    setMatchType(option.id);
  }

  return (
    <Modal
      allowOverflow={true}
      description={t(
        'Separate keywords with commas or new lines. They all take the match type you choose, and Ad Grant policy blocks single-word keywords.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={[
        <Button key={'cancel'} onClick={handleClose}>
          {/* Once rows have been committed there is nothing left to cancel. */}
          {hasAdded ? t('Done') : t('Cancel')}
        </Button>,
        <Button
          disabled={!hasEntries}
          key={'add'}
          name={'confirm-bulk-add'}
          onClick={handleAdd}
          type={'primary'}
        >
          {t('Add keywords')}
        </Button>,
      ]}
      title={t('Bulk add keywords')}
    >
      <Flex direction={'column'} gap={theme.spacing['4']}>
        <Textarea
          label={t('Keywords')}
          onChange={handleRawChange}
          placeholder={t('One keyword per line')}
          rows={6}
          value={raw}
        />
        <UniversalSelect<IMatchTypeOption>
          label={t('Match type')}
          name={'bulk-keyword-match-type'}
          onSelectSingle={handleMatchTypeChange}
          options={matchTypeOptions}
          value={selectedMatchType}
          wrapperClassName={styles.bulkMatchType}
        />
        {summaries.map((summary) => (
          <Alert key={summary.title} title={summary.title} type={summary.type}>
            {summary.keywords}
          </Alert>
        ))}
      </Flex>
    </Modal>
  );
}

export default BulkAddKeywordsModal;
